import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { selectFirestoreSession } from '@store/actionSlices/firestore'
import {
  addMirroredScreen,
  removeMirroredScreen,
  selectDisplayScreens,
  selectMirroredScreens,
  updateDisplayScreenProject,
} from '@store/actionSlices/multiScreen'
import { selectProjectIdentity } from '@store/actionSlices/projectIdentity'
import type { DisplayScreen } from '@store/types'

import FirebaseControlQuery from '@firebaseUtil/firebase-control-query'
import { SessionMap } from '@firebaseUtil/types'

import { notifyError, notifySuccess } from '@adUtilities/notifier'

import CollapsedPanel from './components/collapsed-panel/CollapsedPanel'
import ExpandedPanel from './components/expanded-panel/ExpandedPanel'
import { DisplayScreenStateMap } from './types'

function MultiScreenControlPanel() {
  const dispatch = useDispatch()

  const projectIdentity = useSelector(selectProjectIdentity)
  const session = useSelector(selectFirestoreSession)
  const displayScreens = useSelector(selectDisplayScreens)
  const mirroredScreens = useSelector(selectMirroredScreens)

  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
  })

  const [isCollapsed, setIsCollapsed] = useState(true)
  const [openSections, setOpenSections] = useState<DisplayScreenStateMap>({})
  const [mirroringLoading, setMirroringLoading] =
    useState<DisplayScreenStateMap>({})
  const [pushToScreenLoading, setPushToScreenLoading] =
    useState<DisplayScreenStateMap>({})

  const pushToScreenQueueRef = useRef(Promise.resolve())
  const mirroringQueueRef = useRef(Promise.resolve())

  const mirroringScreens = useMemo<DisplayScreenStateMap>(
    () =>
      Object.fromEntries(mirroredScreens.map((masterKey) => [masterKey, true])),
    [mirroredScreens]
  )

  const toggleSection = useCallback((section: string) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }))
  }, [])

  const getActiveSessionByProject = (
    displayScreen: DisplayScreen,
    projectId?: string
  ) => {
    const projectIdToUse = projectId || projectIdentity.projectName

    const activeSession = displayScreen.sessionKeys.find(
      (screen) => screen.projectId === projectIdToUse
    )

    if (!activeSession) {
      throw new Error(`No session found for ${projectIdToUse}`)
    }

    return activeSession
  }

  const getErrorMessage = (error: unknown, fallbackMessage: string) => {
    if (error instanceof Error && error.message.includes('No session found')) {
      return error.message
    }

    return fallbackMessage
  }

  const handleProjectSwitchAndPush = async (
    displayScreen: DisplayScreen,
    currentSession: SessionMap
  ) => {
    const switchedProjectSession = getActiveSessionByProject(displayScreen)
    if (!switchedProjectSession) {
      throw new Error(`No session found for ${projectIdentity.projectLabel}`)
    }

    const currentShowcaseSession = getActiveSessionByProject(
      displayScreen,
      displayScreen.currentProject
    )
    if (!currentShowcaseSession) {
      throw new Error(`No session found for ${displayScreen.currentProject}`)
    }

    await firebaseControlQuery.update(
      {
        projectSwitch: {
          projectId: projectIdentity.projectUUID,
          projectName: projectIdentity.projectName,
          projectLabel: projectIdentity.projectLabel,
        },
      },
      currentShowcaseSession.key,
      currentShowcaseSession.projectId
    )

    await firebaseControlQuery.update(
      {
        projectSwitch: {
          projectId: '',
          projectName: '',
          projectLabel: '',
        },
      },
      currentShowcaseSession.key,
      currentShowcaseSession.projectId
    )

    dispatch(
      updateDisplayScreenProject({
        masterKey: displayScreen.masterKey,
        projectName: projectIdentity.projectName,
      })
    )

    await firebaseControlQuery.update(
      currentSession,
      switchedProjectSession.key
    )
  }

  const hasProjectSwitched = (displayScreen: DisplayScreen): boolean =>
    projectIdentity.projectName !== displayScreen.currentProject

  const handlePushToScreen = async (displayScreen: DisplayScreen) => {
    if (!session) {
      return
    }

    const { masterKey, label } = displayScreen

    setPushToScreenLoading((prev) => ({
      ...prev,
      [masterKey]: true,
    }))

    pushToScreenQueueRef.current = pushToScreenQueueRef.current.then(
      async () => {
        try {
          if (hasProjectSwitched(displayScreen)) {
            await handleProjectSwitchAndPush(displayScreen, session)
          } else {
            const activeSession = getActiveSessionByProject(displayScreen)
            await firebaseControlQuery.update(session, activeSession.key)
          }

          notifySuccess(`Pushed to screen ${label} successfully`, true)
        } catch (error) {
          notifyError(
            getErrorMessage(error, `Failed to push to screen ${label}`),
            true
          )
        } finally {
          setPushToScreenLoading((prev) => ({
            ...prev,
            [masterKey]: false,
          }))
        }
      }
    )
  }

  const handleMirroring = async (displayScreen: DisplayScreen) => {
    if (!session) {
      return
    }

    const { masterKey, label } = displayScreen

    setMirroringLoading((prev) => ({
      ...prev,
      [masterKey]: true,
    }))

    const isCurrentlyMirrored = mirroredScreens.includes(masterKey)
    if (isCurrentlyMirrored) {
      dispatch(removeMirroredScreen(masterKey))
      setMirroringLoading((prev) => ({
        ...prev,
        [masterKey]: false,
      }))
      return
    }

    mirroringQueueRef.current = mirroringQueueRef.current.then(async () => {
      try {
        if (hasProjectSwitched(displayScreen)) {
          await handleProjectSwitchAndPush(displayScreen, session)

          dispatch(addMirroredScreen(masterKey))
        } else {
          const activeSession = getActiveSessionByProject(displayScreen)
          await firebaseControlQuery.update(session, activeSession.key)

          dispatch(addMirroredScreen(masterKey))
        }

        notifySuccess(`Mirroring started for ${label}`, true)
      } catch (error) {
        notifyError(
          getErrorMessage(
            error,
            `Something went wrong while mirroring ${label}`
          ),
          true
        )
      } finally {
        setMirroringLoading((prev) => ({
          ...prev,
          [masterKey]: false,
        }))
      }
    })
  }

  const syncMirroredScreens = async () => {
    if (!session || !mirroredScreens.length) {
      return
    }

    await Promise.allSettled(
      mirroredScreens.map(async (masterKey) => {
        try {
          const displayScreen = displayScreens.find(
            (screen) => screen.masterKey === masterKey
          )

          if (!displayScreen) {
            console.warn(`Display screen with masterKey ${masterKey} not found`)
            return
          }

          if (hasProjectSwitched(displayScreen)) {
            await handleProjectSwitchAndPush(displayScreen, session)
          } else {
            const activeSession = getActiveSessionByProject(displayScreen)
            await firebaseControlQuery.update(session, activeSession.key)
          }
        } catch (error) {
          notifyError(
            getErrorMessage(
              error,
              `Something went wrong while syncing mirroed screen`
            ),
            true
          )
        }
      })
    )
  }

  useEffect(() => {
    syncMirroredScreens()
  }, [session])

  return (
    <div className="fixed right-4 top-1/2 z-40 -translate-y-1/2">
      {isCollapsed ? (
        <CollapsedPanel
          mirroringScreens={mirroringScreens}
          onToggleCollapse={setIsCollapsed}
        />
      ) : (
        <ExpandedPanel
          mirroringScreens={mirroringScreens}
          openSections={openSections}
          mirroringLoading={mirroringLoading}
          pushToScreenLoading={pushToScreenLoading}
          onToggleCollapse={setIsCollapsed}
          onToggleSection={toggleSection}
          onMirroring={handleMirroring}
          onPushToScreen={handlePushToScreen}
        />
      )}
    </div>
  )
}

export default MultiScreenControlPanel
